import { useState, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils/cn';
import { extractVariables } from '../utils/formula-parser';
import { AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface FormulaEditorProps {
  value: string;
  onChange: (value: string) => void;
  availableVariables: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

interface FormulaToken {
  type: 'variable' | 'operator' | 'number' | 'parenthesis' | 'whitespace' | 'text';
  value: string;
  isValid?: boolean;
}

export function FormulaEditor({
  value,
  onChange,
  availableVariables,
  placeholder,
  disabled,
  className,
}: FormulaEditorProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredVariables, setFilteredVariables] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [unknownVariables, setUnknownVariables] = useState<string[]>([]);
  const [tokens, setTokens] = useState<FormulaToken[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Tokenizar la fórmula para el preview visual
  const tokenizeFormula = (formula: string): FormulaToken[] => {
    if (!formula) return [];

    const tokens: FormulaToken[] = [];
    const singleOperators = ['+', '-', '*', '/', '^', '%'];
    const parenthesis = ['(', ')'];
    let i = 0;

    while (i < formula.length) {
      const char = formula[i];

      // Espacios
      if (char === ' ') {
        tokens.push({ type: 'whitespace', value: ' ' });
        i++;
        continue;
      }

      // Paréntesis
      if (parenthesis.includes(char)) {
        tokens.push({ type: 'parenthesis', value: char });
        i++;
        continue;
      }

      // Multi-character operators: ==, !=, >=, <=, &&, ||
      if (i + 1 < formula.length) {
        const twoChar = formula.slice(i, i + 2);
        if (['==', '!=', '>=', '<=', '&&', '||'].includes(twoChar)) {
          tokens.push({ type: 'operator', value: twoChar });
          i += 2;
          continue;
        }
      }

      // Single-character comparison/logical/ternary operators: >, <, !, ?, :
      if (['>', '<', '!', '?', ':'].includes(char)) {
        tokens.push({ type: 'operator', value: char });
        i++;
        continue;
      }

      // Arithmetic operators
      if (singleOperators.includes(char)) {
        tokens.push({ type: 'operator', value: char });
        i++;
        continue;
      }

      // Números
      if (/[0-9]/.test(char)) {
        let num = '';
        while (i < formula.length && /[0-9.]/.test(formula[i])) {
          num += formula[i];
          i++;
        }
        tokens.push({ type: 'number', value: num });
        continue;
      }

      // Variables (mayúsculas, guión bajo)
      if (/[A-Z_]/.test(char)) {
        let variable = '';
        while (i < formula.length && /[A-Z0-9_]/.test(formula[i])) {
          variable += formula[i];
          i++;
        }
        const isValid = availableVariables.includes(variable);
        tokens.push({ type: 'variable', value: variable, isValid });
        continue;
      }

      // Texto desconocido
      tokens.push({ type: 'text', value: char });
      i++;
    }

    return tokens;
  };

  // Detectar variables desconocidas y tokenizar
  useEffect(() => {
    if (value) {
      const usedVariables = extractVariables(value);
      const unknown = usedVariables.filter((v) => !availableVariables.includes(v));
      setUnknownVariables(unknown);
      setTokens(tokenizeFormula(value));
    } else {
      setUnknownVariables([]);
      setTokens([]);
    }
  }, [value, availableVariables]);

  // Detectar la palabra actual bajo el cursor
  const getCurrentWord = (text: string, position: number): { word: string; startPos: number } => {
    const beforeCursor = text.slice(0, position);
    const afterCursor = text.slice(position);

    // Buscar el inicio de la palabra (letras mayúsculas, números y guión bajo)
    const startMatch = beforeCursor.match(/[A-Z_][A-Z0-9_]*$/);
    const start = startMatch ? position - startMatch[0].length : position;

    // Buscar el final de la palabra
    const endMatch = afterCursor.match(/^[A-Z0-9_]*/);
    const end = endMatch ? position + endMatch[0].length : position;

    const word = text.slice(start, end);
    return { word, startPos: start };
  };

  // Actualizar sugerencias cuando cambia el valor o la posición del cursor
  useEffect(() => {
    if (!value || cursorPosition === 0) {
      setShowSuggestions(false);
      return;
    }

    const { word } = getCurrentWord(value, cursorPosition);

    if (word.length >= 1) {
      const filtered = availableVariables.filter((v) =>
        v.toUpperCase().startsWith(word.toUpperCase())
      );

      if (filtered.length > 0 && word !== filtered[0]) {
        setFilteredVariables(filtered);
        setShowSuggestions(true);
        setSelectedIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  }, [value, cursorPosition, availableVariables]);

  // Insertar variable seleccionada
  const insertVariable = (variable: string) => {
    const { word, startPos } = getCurrentWord(value, cursorPosition);
    const before = value.slice(0, startPos);
    const after = value.slice(startPos + word.length);
    const newValue = before + variable + after;

    onChange(newValue);
    setShowSuggestions(false);

    // Mover el cursor al final de la variable insertada
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = startPos + variable.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Manejar teclas especiales
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredVariables.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredVariables.length) % filteredVariables.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertVariable(filteredVariables[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Actualizar posición del cursor
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    setCursorPosition(e.target.selectionStart);
  };

  const handleClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    setCursorPosition(e.currentTarget.selectionStart);
  };

  // Renderizar badge según el tipo de token
  const renderToken = (token: FormulaToken, index: number) => {
    switch (token.type) {
      case 'variable':
        return (
          <Badge
            key={index}
            className={cn(
              'font-mono text-[11px] px-2 py-0.5 rounded',
              token.isValid
                ? 'bg-info text-white border-info hover:bg-info'
                : 'bg-bloqueo text-white border-bloqueo hover:bg-bloqueo'
            )}
          >
            {token.value}
          </Badge>
        );
      case 'operator':
        return (
          <span key={index} className="font-mono text-muted-foreground font-bold px-1 text-base">
            {token.value}
          </span>
        );
      case 'number':
        return (
          <Badge
            key={index}
            className="font-mono text-[11px] px-2 py-0.5 rounded bg-alerta text-white border-alerta hover:bg-alerta"
          >
            {token.value}
          </Badge>
        );
      case 'parenthesis':
        return (
          <span key={index} className="font-mono text-purple-600 font-bold text-lg leading-none">
            {token.value}
          </span>
        );
      case 'whitespace':
        return <span key={index}> </span>;
      default:
        return (
          <span key={index} className="font-mono text-muted-foreground">
            {token.value}
          </span>
        );
    }
  };

  return (
    <div className="relative space-y-2">
      {/* Preview Visual con Badges */}
      {value && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Vista previa:</p>
          <div className="p-3 rounded-md border-2 border-border bg-gradient-to-br from-gray-50 to-gray-100 min-h-[60px] flex flex-wrap items-center gap-1.5">
            {tokens.length > 0 ? (
              tokens.map((token, index) => renderToken(token, index))
            ) : (
              <span className="text-sm text-muted-foreground italic">Escribe una fórmula...</span>
            )}
          </div>
        </div>
      )}

      {/* Editor de Texto */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Editor:</p>
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn('font-mono', className)}
          rows={3}
        />
      </div>

      {showSuggestions && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          <div className="p-1">
            {filteredVariables.map((variable, index) => (
              <div
                key={variable}
                className={cn(
                  'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
                  index === selectedIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                )}
                onClick={() => insertVariable(variable)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className="font-mono font-medium">{variable}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leyenda de colores */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground pt-1">
        <div className="flex items-center gap-1">
          <Badge className="bg-info text-white text-[10px] px-1.5 py-0">VAR</Badge>
          <span>Variables válidas</span>
        </div>
        <div className="flex items-center gap-1">
          <Badge className="bg-bloqueo text-white text-[10px] px-1.5 py-0">VAR</Badge>
          <span>Variables inválidas</span>
        </div>
        <div className="flex items-center gap-1">
          <Badge className="bg-alerta text-white text-[10px] px-1.5 py-0">123</Badge>
          <span>Números</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="font-mono font-bold text-muted-foreground">+</span>
          <span>Operadores</span>
        </div>
      </div>

      {unknownVariables.length > 0 ? (
        <div className="mt-2 p-2 rounded-md bg-destructive/10 border border-destructive/20 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
          <div className="text-sm text-destructive">
            <p className="font-medium">Variables no definidas:</p>
            <p className="mt-1">{unknownVariables.join(', ')}</p>
            <p className="mt-1 text-xs">
              Estas variables no existen. Usa solo variables disponibles o créalas primero.
            </p>
          </div>
        </div>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          💡 Escribe el nombre de una variable para ver sugerencias. Usa ↑↓ para navegar y Enter/Tab
          para insertar.
        </p>
      )}
    </div>
  );
}
